import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useInView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';
import { useMedia } from 'react-use';
import {
  Toast,
  ToastContainer,
  ToastVariant,
  useToast,
} from '@hungryroot/components-toast';

import { useExperiment } from '@hungryroot/experiments';
import { FlexCreditCheckoutMessage } from '@hungryroot/flex-credits';
import {
  OrderSummaryFooterOverlay,
  OrderSummaryTotalAmount,
  CalloutBoxSelector,
  OrderFrequencySelector,
} from '@hungryroot/funnel';
import { PlanContent, type MealContainerSubmitProps } from '@hungryroot/funnel';
import { useGoogleAnalytics } from '@hungryroot/google-analytics';
import { Calendar2, CartFilled, FilterBadge } from '@hungryroot/icons';
import { OneColTwoCol } from '@hungryroot/layout';
import {
  usePricingServiceCustomerPlansPrice,
  invalidatePlanServiceCustomerPlansPrice,
  usePlanNameHeaderIcon,
  useCustomerCheckoutPricingInfo,
  usePlanServiceCustomerPlanFullEdit,
  usePlanServiceNestedUniverse,
  usePostCustomerPlanRecommender,
  invalidateCustomerCheckoutPricingInfo,
  setNextPlanComponents,
  convertPlanComponentsToEdit,
  setNextPlanComponentsForRecipeSize,
  KIDS_STABLE_NAME_ID,
  setNextPlanComponentsForNonMealContainers,
  setNextPlanComponentsForQuantityContainers,
  usePlanServiceCustomerPlansView,
  invalidatePlanServiceCustomerPlansView,
  CommonQuestions,
  getPromoForOverlay,
  getSpendMoreNudgeForOverlay,
  getApplicableNudgePromo,
  getSpendMoreNudge,
  HowItWorks,
  getApplicablePromo,
  getPromos,
  getCyoPromo,
  getContainerIdToPlanComponent,
  getContainerIdToContainer,
} from '@hungryroot/plan';
import { Button, Heading, Paragraph } from '@hungryroot/ramen';
import { type Customer, type ServingSize } from '@hungryroot/rest';
import { CHECKOUT_SHIPPING_ROUTE } from '@hungryroot/routes';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import {
  OrderSummary,
  getNumPromosWithDollarEffect,
} from '../OrderSummary/OrderSummary';

import styles from './PrecheckoutPlan.module.css';

type Props = {
  testid?: string;
  customer: Partial<Pick<Customer, 'first_name' | 'id' | 'cart'>>;
  navBar: ReactNode;
};

export function PrecheckoutPlan(props: Props) {
  const { testid, customer, navBar } = props;
  const orderId = customer.cart;

  const showFlexCreditMessage = useExperiment(164) === 1;
  const dietDrivenPreferencesVariant = useExperiment(175);

  const isDietDrivenPreferencesVariant =
    dietDrivenPreferencesVariant !== undefined &&
    dietDrivenPreferencesVariant > 0;

  const budgetRangePlanVariant = useExperiment(177);
  const isInBudgetRangePlanVariant =
    budgetRangePlanVariant !== undefined && budgetRangePlanVariant > 0;

  const biweeklyPlanVariant = useExperiment(183);
  const isInBiweeklyPlanVariant =
    biweeklyPlanVariant !== undefined && biweeklyPlanVariant > 0;

  const { ref: shippingFreeVisibleRef, inView: shippingFreeVisible } =
    useInView();

  const { ref: shippingVisibleRef, inView: shippingVisible } = useInView();

  const location = useLocation();

  const { pageViewTrackingGA } = useGoogleAnalytics();

  useEffect(() => {
    pageViewTrackingGA(location.pathname);
  }, []);

  const isDesktop = useMedia(BreakpointQueryLarge);
  const queryClient = useQueryClient();
  const postCustomerPlanRecommender = usePostCustomerPlanRecommender();
  const customerId = customer.id || 0;

  const [savePlanError, setSavePlanError] = useState('');

  useEffect(() => {
    postCustomerPlanRecommender.mutate({
      customerId,
    });
  }, []);

  const savePlan = usePlanServiceCustomerPlanFullEdit();

  const { data: checkoutPricing, isLoading: isLoadingCheckoutPricing } =
    useCustomerCheckoutPricingInfo({
      enabled: postCustomerPlanRecommender.isSuccess,
    });

  const { data: planServiceNestedUniverseData } = usePlanServiceNestedUniverse(
    {
      is_available_pre_checkout: 1,
    },
    {
      enabled: postCustomerPlanRecommender.isSuccess,
    }
  );

  const {
    data: customerPlanComponentsData,
    isLoading: isLoadingCustomerPlanComponents,
  } = usePlanServiceCustomerPlansView(
    { customerId },
    {
      enabled: postCustomerPlanRecommender.isSuccess,
    }
  );

  const payAsYouGoExperiment = useExperiment(173);

  const isInPayAsYouGoExperiment =
    payAsYouGoExperiment !== undefined && payAsYouGoExperiment > 0;

  const biweeklyExperiment = useExperiment(183);
  const isInBiweeklyExperiment =
    biweeklyExperiment !== undefined && biweeklyExperiment > 0;

  const lunchRefreshUi = usePlanNameHeaderIcon(customerPlanComponentsData);

  const [planComponents, setPlanComponents] = useState(
    customerPlanComponentsData
  );

  const {
    data: plansPriceData,
    isLoading: isLoadingPlansPrice,
    isFetching: isFetchingPlansPrice,
  } = usePricingServiceCustomerPlansPrice(
    {},
    {
      enabled: postCustomerPlanRecommender.isSuccess,
    }
  );

  useEffect(() => {
    setPlanComponents(customerPlanComponentsData);
  }, [customerPlanComponentsData]);

  const [belowPromoThresholdToastShown, setBelowPromoThresholdToastShown] =
    useState(false);

  const { toast } = useToast();

  const [prevSubtotal, setPrevSubtotal] = useState<number | undefined>();

  const hideFooterOverlay =
    shippingVisible === true || shippingFreeVisible === true;

  // API returns these as strings but the client needs to do math operations in two scenarios
  // subtotal + shipping
  // determining the promo nudges (we rely on comparisons between subtotal and the promo thresholds
  // to determine what spend nudges to show to the customer).
  const numSubtotal = Number(checkoutPricing?.first_box_pricing?.subtotal || 0);
  const numShipping = Number(
    checkoutPricing?.first_box_pricing?.shipping_cost || 0
  );
  const numTotal = Number(checkoutPricing?.first_box_pricing?.total || 0);
  const numDiscount = Number(checkoutPricing?.first_box_pricing?.discount || 0);
  const promoEffect = checkoutPricing?.first_box_promo_effect;
  const promoList = promoEffect?.promos;
  const promoEffectBase = promoEffect?.effect_base;

  // we cannot use this promoEffectString directly because it shows
  // Offer Applied: free snack or sweet for life + free shipping. Spend $99 for 30% off your first delivery!
  // and we just want to show Spend $99 for 30% off your first delivery!
  const promoEffectString = promoEffect?.effect_str;
  const evergreenThreshold = checkoutPricing?.evergreen_threshold || 0;
  const lowerBudget = Number(checkoutPricing?.lower_bound);
  const upperBudget = Number(checkoutPricing?.upper_bound);
  const freeShippingDelta = checkoutPricing?.free_shipping_delta;

  useEffect(() => {
    if (numSubtotal) {
      if (!prevSubtotal || prevSubtotal !== numSubtotal) {
        setPrevSubtotal(numSubtotal);
      }
    }
  }, [numSubtotal]);

  // Disable edit buttons while fetching plans price
  const queriesInFlight = savePlan.isPending || isFetchingPlansPrice;

  const promos = getPromos(promoList, numDiscount);
  const applicablePromo = getApplicablePromo(promos);
  const cyoPromo = getCyoPromo(promos);
  const nudgePromo = getApplicableNudgePromo(applicablePromo);
  const spendMoreNudge = getSpendMoreNudge(
    numSubtotal,
    numShipping,
    nudgePromo
  );

  useEffect(() => {
    if (
      promoList &&
      numDiscount !== undefined &&
      numSubtotal !== undefined &&
      prevSubtotal !== undefined
    ) {
      const promos = getPromos(promoList, numDiscount);
      const applicablePromo = getApplicablePromo(promos);
      const nudgePromo = getApplicableNudgePromo(applicablePromo);
      if (
        spendMoreNudge &&
        nudgePromo &&
        // previous subtotal more than threshold
        prevSubtotal >= nudgePromo?.threshold &&
        // new subtotal is less than threshold
        numSubtotal < nudgePromo?.threshold &&
        // toast hasn't been shown yet
        belowPromoThresholdToastShown === false
      ) {
        toast(
          <Toast className={styles.toast} variant={ToastVariant.Critical}>
            {`The promo applied has been removed. ${spendMoreNudge}`}
          </Toast>
        );
        setBelowPromoThresholdToastShown(true);
      } else {
        setBelowPromoThresholdToastShown(false);
      }
    }
  }, [promoList, numDiscount, numSubtotal, prevSubtotal, spendMoreNudge]);

  const promoText = getPromoForOverlay(
    numSubtotal,
    promoEffectBase,
    applicablePromo,
    cyoPromo
  );

  const amountSummary = (
    <OrderSummaryTotalAmount
      testid="pre-checkout-plan-amount-summary"
      total={numTotal}
      subtotal={
        getNumPromosWithDollarEffect(numSubtotal, promos)
          ? numSubtotal
          : undefined
      }
    />
  );

  const actionButton = (
    <Button
      loading={queriesInFlight}
      className={`${styles.checkout} ${
        spendMoreNudge
          ? styles['checkout-with-nudge']
          : styles['checkout-without-nudge']
      } `}
      size="large"
      prominence="primary"
      style="default"
      textPrimary="Head to checkout"
      testid="head-to-checkout-button"
      buttonType="link"
      disabled={queriesInFlight}
      to={CHECKOUT_SHIPPING_ROUTE}
    />
  );
  const orderSummary = (
    <div>
      <OrderSummary
        subtotalLabel={
          isInBudgetRangePlanVariant ||
          isInPayAsYouGoExperiment ||
          isInBiweeklyExperiment
            ? 'Subtotal'
            : 'Weekly plan'
        }
        freeShippingDelta={freeShippingDelta}
        showPricingNudge={
          numSubtotal < evergreenThreshold && freeShippingDelta === null
            ? Boolean(promoEffectString)
            : undefined
        }
        testid="pre-checkout-plan-order-summary"
        summaryText="Order summary"
        shippingVisibleRef={shippingVisibleRef}
        shippingFreeVisibleRef={shippingFreeVisibleRef}
        orderId={orderId}
        containerClassName={clsx(styles['order-summary'], {
          [styles['order-summary-variant']]: isDietDrivenPreferencesVariant,
        })}
        amountSummary={amountSummary}
        subtotal={numSubtotal}
        shipping={numShipping}
        promos={promos}
        hidePromo
        totalText="First order total"
        flexCreditMessaging={showFlexCreditMessage}
      >
        {actionButton}
        {savePlanError && (
          <Paragraph
            testid={`${testid}-error-message`}
            className={styles.error}
            variant="caption"
          >
            {savePlanError}
          </Paragraph>
        )}
      </OrderSummary>
      {(isDesktop || (isInPayAsYouGoExperiment && !isDesktop)) && (
        <CalloutBoxSelector />
      )}
      <FlexCreditCheckoutMessage />
    </div>
  );

  const orderSummaryFooterOverlay = (
    <OrderSummaryFooterOverlay
      summaryText="Order summary"
      promo={promoText}
      spendMoreNudge={getSpendMoreNudgeForOverlay(
        numShipping,
        numSubtotal,
        applicablePromo,
        promoText
      )}
      amountSummary={amountSummary}
      button={actionButton}
    />
  );

  const onPlanChangeSuccess = () => {
    invalidateCustomerCheckoutPricingInfo(queryClient);
    invalidatePlanServiceCustomerPlansView(queryClient, { customerId });
    invalidatePlanServiceCustomerPlansPrice(queryClient, {});
    setSavePlanError('');
  };

  const onError = () => {
    setSavePlanError('Something went wrong, please try again.');
  };

  if (
    isLoadingCheckoutPricing ||
    !checkoutPricing ||
    isLoadingCustomerPlanComponents ||
    !planComponents ||
    !customer?.id ||
    !planServiceNestedUniverseData ||
    isLoadingPlansPrice ||
    !plansPriceData ||
    !lunchRefreshUi
  ) {
    return null;
  }

  const containerIdToContainer = getContainerIdToContainer(plansPriceData);
  const containerIdToPlanComponent =
    getContainerIdToPlanComponent(planComponents);

  return (
    <div data-testid={testid} className={styles.container}>
      <div className={styles.inner}>
        <div className={styles.plan}>
          {navBar}
          <OneColTwoCol
            leftCol={
              <>
                {isDietDrivenPreferencesVariant ? (
                  <HowItWorks
                    title="How it works"
                    items={[
                      {
                        icon: <CartFilled />,
                        highlightedText: 'We’ll fill your cart',
                        text: 'with groceries and recipes that fit your preferences + tastes.',
                      },
                      {
                        icon: <Calendar2 />,
                        highlightedText: 'You’re in control',
                        text: '– edit, skip, or reschedule your delivery before it ships.',
                      },
                      {
                        icon: <FilterBadge />,
                        highlightedText: 'Your plan is flexible',
                        text: '– set it below, and easily adjust it each week.',
                      },
                    ]}
                    className={styles['how-it-works']}
                  />
                ) : isInBudgetRangePlanVariant ? (
                  <HowItWorks
                    className={styles['how-it-works']}
                    title="How it works"
                    subtitle={`Your weekly budget: $${lowerBudget} – $${upperBudget}`}
                    items={[
                      {
                        icon: <CartFilled />,
                        highlightedText: 'We’ll fill your cart',
                        text: 'with groceries and recipes that fit your budget + tastes.',
                      },
                      {
                        icon: <Calendar2 />,
                        highlightedText: 'You’re in control',
                        text: '– edit, skip, or reschedule your delivery before it ships.',
                      },
                      {
                        icon: <FilterBadge />,
                        highlightedText: 'Your budget is flexible',
                        text: '– set it below, and easily adjust it each week.',
                      },
                    ]}
                  ></HowItWorks>
                ) : null}
                <div
                  className={clsx({
                    [styles['plan-content']]:
                      isInBudgetRangePlanVariant && isDesktop,
                  })}
                >
                  {isInBudgetRangePlanVariant && (
                    <>
                      <Heading className={styles.heading} variant="H4">
                        Here's what to expect
                      </Heading>
                      <Paragraph className={styles.paragraph}>
                        Make any changes to update your weekly budget.
                      </Paragraph>
                    </>
                  )}
                  {planServiceNestedUniverseData.occasion_groups.map(
                    ({ id, name, occasions, sub_header, tooltip }, index) => {
                      if (name === KIDS_STABLE_NAME_ID) {
                        return;
                      }

                      return (
                        <PlanContent
                          showSubsections={!isInBudgetRangePlanVariant}
                          key={id}
                          id={id}
                          name={lunchRefreshUi.getName(name)}
                          editDisabled={queriesInFlight}
                          customerId={customerId}
                          occasions={occasions}
                          subHeader={lunchRefreshUi.getSubHeader({
                            name,
                            subHeader: sub_header,
                          })}
                          tooltip={tooltip}
                          index={index}
                          planServiceNestedUniverseData={
                            planServiceNestedUniverseData
                          }
                          containerIdToContainer={containerIdToContainer}
                          planComponents={planComponents}
                          confirmButtonText="Update"
                          onMealContainerSubmit={({
                            nextNumLunch,
                            nextNumDinner,
                            numLunch,
                            numDinner,
                            occasionId,
                            containerId,
                            dinnerContainerId,
                            servingSize,
                          }: MealContainerSubmitProps) => {
                            // need to handle cases where the user has set the number of dinner or lunch recipes to 0
                            // and they want to add one of them back

                            const effectiveNextPlanComponents =
                              setNextPlanComponents({
                                planComponents,
                                nextNumLunch,
                                nextNumDinner,
                                numLunch,
                                numDinner,
                                occasionId,
                                containerId,
                                dinnerContainerId,
                                id,
                                servingSize,
                              });

                            savePlan.mutate(
                              {
                                customerId,
                                body: {
                                  actor: 'FUNNEL',
                                  plan_components: convertPlanComponentsToEdit(
                                    effectiveNextPlanComponents
                                  ),
                                },
                              },
                              {
                                onSuccess: onPlanChangeSuccess,
                                onError,
                              }
                            );
                          }}
                          onRecipeSizeContainerSubmit={({
                            value,
                            numLunch,
                            numDinner,
                          }: {
                            value: ServingSize;
                            numLunch: number;
                            numDinner: number;
                          }) => {
                            const nextPlanComponents =
                              setNextPlanComponentsForRecipeSize({
                                planComponents,
                                value,
                                numLunch,
                                numDinner,
                              });
                            savePlan.mutate(
                              {
                                customerId,
                                body: {
                                  actor: 'FUNNEL',
                                  plan_components:
                                    convertPlanComponentsToEdit(
                                      nextPlanComponents
                                    ),
                                },
                              },
                              {
                                onSuccess: onPlanChangeSuccess,
                                onError,
                              }
                            );
                          }}
                          onNonMealContainerSubmit={({
                            value,
                            containerId,
                            occasionId,
                            occasionGroupId,
                          }: {
                            value: string;
                            containerId: number;
                            occasionId: number;
                            occasionGroupId: number;
                          }) => {
                            const effectiveNextPlanComponents =
                              setNextPlanComponentsForNonMealContainers({
                                value,
                                planComponents,
                                containerId,
                                containerIdToPlanComponent,
                                occasionId,
                                occasionGroupId,
                              });

                            savePlan.mutate(
                              {
                                customerId,
                                body: {
                                  actor: 'FUNNEL',
                                  plan_components: convertPlanComponentsToEdit(
                                    effectiveNextPlanComponents
                                  ),
                                },
                              },
                              {
                                onSuccess: onPlanChangeSuccess,
                                onError,
                              }
                            );
                          }}
                          onContainerQuantitySubmit={({
                            quantity,
                            servingSize,
                            dinnerServingSize,
                            containerId,
                            occasionId,
                            occasionGroupId,
                          }: {
                            quantity: number;
                            servingSize: ServingSize;
                            dinnerServingSize: ServingSize;
                            containerId: number;
                            occasionId: number;
                            occasionGroupId: number;
                          }) => {
                            const effectiveNextPlanComponents =
                              setNextPlanComponentsForQuantityContainers({
                                quantity,
                                servingSize,
                                dinnerServingSize,
                                planComponents,
                                containerId,
                                containerIdToPlanComponent,
                                occasionId,
                                occasionGroupId,
                              });

                            savePlan.mutate(
                              {
                                customerId,
                                body: {
                                  actor: 'FUNNEL',
                                  plan_components: convertPlanComponentsToEdit(
                                    effectiveNextPlanComponents
                                  ),
                                },
                              },
                              {
                                onSuccess: onPlanChangeSuccess,
                                onError,
                              }
                            );
                          }}
                        />
                      );
                    }
                  )}
                </div>
                {isInBiweeklyPlanVariant && <OrderFrequencySelector />}
                {isDesktop === false && orderSummary}
                <section>
                  <CommonQuestions />
                </section>
                {isDesktop === false && (
                  <div
                    className={
                      hideFooterOverlay
                        ? styles['footer-overlay']
                        : styles['footer-overlay-visible']
                    }
                  >
                    {orderSummaryFooterOverlay}
                  </div>
                )}
              </>
            }
            rightCol={orderSummary}
          ></OneColTwoCol>
        </div>
      </div>
      <ToastContainer position="top-center" />
    </div>
  );
}
